import { useCallback, useEffect, useRef } from 'react';

export const useInfiniteTreeScroll = (
  scrollContainerReference: React.RefObject<HTMLElement | null>,
  {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  }: {
    hasNextPage?: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => void | Promise<unknown>;
  }
) => {
  const sentinelReference = useRef<HTMLDivElement>(null);

  const handleFetchNextPage = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const sentinel = sentinelReference.current;
    if (!sentinel || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) handleFetchNextPage();
      },
      { root: scrollContainerReference.current, rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, handleFetchNextPage, scrollContainerReference]);

  return { sentinelRef: sentinelReference, hasNextPage };
};
